import classNames from "classnames";
import { useEffect, useState } from "react";
import { useBeforeUnload, useNavigate, useParams } from "react-router-dom";
import { EventCardSkeleton } from "../../../components/event/eventCard";
import { Icon } from "../../../components/icon/icon";
import { BareLayout } from "../../../components/layout/bareLayout";
import { Protected } from "../../../components/layout/protected";
import { ProgressBar } from "../../../components/progressBar";
import { usePrompt } from "../../../customeHooks/usePrompt";
import {
  useEditEventMutation,
  useGetEventGuestsQuery,
  useGetEventQuery,
  useGetEventTicketsQuery,
} from "../../../redux/features/event/event.api";
import {
  CreateEvent,
  EventStatus,
} from "../../../redux/features/event/event.types";
import { formatDate, parseDateStringToDate } from "../../../utils/helpers";
import { EventDetails } from "./eventDetails";
import { EventPreview } from "./eventPreview";
import { EventTickets } from "./eventTickets";

const steps = [
  { title: "Event Details", id: 1 },
  { title: "Tickets", id: 2 },
  { title: "Preview", id: 3 },
];

export const EventForm = ({ mode = "create" }: { mode: "create" | "edit" }) => {
  const [finalPayload, setFinalPayload] = useState<CreateEvent>();
  const [step, setStep] = useState<number>(1);
  const [showLivePreview, setShowLivePreview] = useState(false);

  const navigate = useNavigate();

  const { slug } = useParams<{ slug: string }>();

  const [editEvent, { isLoading: editingEvent }] = useEditEventMutation();

  const { data: eventResponse, isFetching } = useGetEventQuery(
    {
      slug: slug,
    },
    { skip: mode === "create", refetchOnMountOrArgChange: true },
  );

  const { data: eventTicketsResponse, isFetching: fetchingTickets } =
    useGetEventTicketsQuery(
      {
        slug: slug,
      },
      { skip: mode === "create", refetchOnMountOrArgChange: true },
    );

  const { data: guests } = useGetEventGuestsQuery(
    {
      id: eventResponse?.data?._id ?? "",
      search: "",
      page: 1,
      limit: 1,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: mode === "create" || !eventResponse?.data?._id,
    },
  );

  useEffect(() => {
    setFinalPayload({
      name: "",
      description: "",
      location: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      image: "",
      type: "physical",
      status: "upcoming",
      tickets: [],
    });
  }, []);

  useEffect(() => {
    if (mode === "edit" && eventResponse?.data && eventTicketsResponse?.data) {
      setFinalPayload({
        name: eventResponse.data.name,
        description: eventResponse.data?.description ?? "",
        location: eventResponse.data.location,
        startDate: formatDate(eventResponse.data.startDate),
        endDate: formatDate(eventResponse.data.endDate),
        startTime: eventResponse.data.startTime,
        endTime: eventResponse.data.endTime,
        image: eventResponse.data.image,
        type: eventResponse.data.type,
        status: eventResponse.data.status,
        tickets: eventTicketsResponse.data.tickets.map((ticket) => {
          return {
            ticketType: ticket.ticketType,
            name: ticket.name,
            price: ticket.price,
            description: ticket.description,
            quantityAvailable: ticket.quantityAvailable,
            quantityType: ticket.quantityType,
          };
        }),
      });
    }
  }, [eventResponse, mode, eventTicketsResponse]);

  useBeforeUnload((e) => {
    e.preventDefault();
    e.returnValue = ""; // Required for Chrome
  });

  usePrompt({
    message: "You have unsaved changes. Are you sure you want to leave?",
  });

  return (
    <Protected>
      <BareLayout
        title={showLivePreview ? "Preview Event" : `${mode} Event`}
        bodyClassName="flex-col gap-6 xl:flex-row xl:gap-0"
        cancelLoading={
          mode === "edit" &&
          eventResponse?.data?.status === "draft" &&
          editingEvent
        }
        cancelText={
          mode === "edit" && eventResponse?.data?.status === "draft"
            ? "Save & Close"
            : "Cancel"
        }
        onCancel={async () => {
          if (mode === "create") {
            // const confirmLeave = window.confirm(
            //   "Changes that you made may not be saved.",
            // );
            // if (confirmLeave) {
            //   navigate("/events");
            // }
            navigate("/events");
          } else if (
            mode === "edit" &&
            eventResponse?.data?.status === "draft"
          ) {
            const payloadData = {
              ...finalPayload,
              startDate:
                parseDateStringToDate(finalPayload!.startDate)?.toISOString() ??
                "",
              endDate:
                parseDateStringToDate(finalPayload!.endDate)?.toISOString() ??
                "",
              status: "draft" as EventStatus,
            };

            const resp = await editEvent({
              id: eventResponse?.data?._id ?? "",
              payload: payloadData,
            });

            if (resp.data?.status === "success") {
              navigate(-1);
            }
          } else {
            navigate(-1);
          }
        }}
      >
        {(mode === "create" && !showLivePreview) ||
        (mode === "edit" &&
          eventResponse?.data?.status === "draft" &&
          !showLivePreview) ? (
          <div className="top-0 hidden h-fit w-full max-w-[200px] flex-col md:sticky xl:flex">
            {steps.map((item) => {
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-2 p-3 text-sm font-bold ${step === item.id && "bg-white"} w-full rounded-2xl`}
                >
                  <Icon
                    svg="check"
                    width={20}
                    height={20}
                    className={`${step > item.id ? "text-success" : "text-grey-400"}`}
                  />
                  {item.title}
                </div>
              );
            })}
          </div>
        ) : undefined}

        {mode === "create" ||
        (mode === "edit" && eventResponse?.data?.status === "draft") ? (
          <div className="flex items-center justify-between gap-4 xl:hidden">
            <p className="text-sm font-bold text-grey-600">Step {step} of 3</p>

            <ProgressBar step={step} total={3} className="max-w-[100px]" />
          </div>
        ) : undefined}

        <div
          className={classNames("flex h-max flex-1 justify-center md:px-10", {
            "md:!px-0": showLivePreview,
          })}
        >
          <div
            className={classNames(`flex w-full flex-col gap-4`, {
              "max-w-[800px]": step === 3 && !showLivePreview,
              "max-w-[580px]": step !== 3 && !showLivePreview,
            })}
          >
            {isFetching || fetchingTickets ? (
              <EventCardSkeleton showExtraDetails imageDimension={300} />
            ) : undefined}

            {step === 1 && finalPayload && !isFetching && !fetchingTickets ? (
              <EventDetails
                finalPayload={finalPayload}
                setFinalPayload={
                  setFinalPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setStep={(s) => {
                  setStep(s);
                }}
                mode={mode}
                id={eventResponse?.data?._id ?? ""}
                ticketsBought={(guests?.data?.totalCount ?? 0) > 0}
              />
            ) : undefined}

            {step === 2 && finalPayload && !isFetching && !fetchingTickets ? (
              <EventTickets
                finalPayload={finalPayload}
                setFinalPayload={
                  setFinalPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setStep={(s) => {
                  setStep(s);
                }}
              />
            ) : undefined}

            {step === 3 && finalPayload && !isFetching && !fetchingTickets ? (
              <EventPreview
                finalPayload={finalPayload}
                setFinalPayload={
                  setFinalPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setStep={(s) => {
                  setStep(s);
                }}
                mode={mode}
                id={eventResponse?.data?._id ?? ""}
                showLivePreview={showLivePreview}
                setShowLivePreview={setShowLivePreview}
              />
            ) : undefined}
          </div>
        </div>
      </BareLayout>
    </Protected>
  );
};
